import { useState } from "react";
import Icon from "../Icon";
import { MaterialNames } from "../material_symbol_names";

const sharedClassName = "object-cover rounded-full";

const sizes = {
  small: { className: "w-6 h-6", size: 16 },
  medium: { className: "w-12 h-12", size: 28 },
  large: { className: "w-[70px] h-[70px]", size: 40 },
  xlarge: { className: "w-[110px] h-[110px]", size: 60 }
};

interface AvatarPlaceholderProps {
  size: keyof typeof sizes;
  className?: string;
  iconClassName?: string;
  iconName?: MaterialNames;
}

function AvatarPlaceholder({ size, className, iconClassName, iconName = "person" }: AvatarPlaceholderProps) {
  const sizeData = sizes[size];

  return (
    <div
      role="img"
      aria-label="Profile photo"
      className={`${sharedClassName} ${sizeData.className} bg-sys-brand-secondary-container flex items-center justify-center ${className}`}
      data-testid="avatar-person-icon"
    >
      <Icon name={iconName} size={sizeData.size} className={iconClassName} />
    </div>
  );
}

interface Props {
  picture?: string;
  size?: keyof typeof sizes;
  className?: string;
  iconClassName?: string;
  iconName?: MaterialNames;
}

export default function Avatar({ picture, size = "medium", className, iconClassName, iconName = "person" }: Props) {
  const [failedPicture, setFailedPicture] = useState<string | undefined>();

  const sizeData = sizes[size];
  const showImage = Boolean(picture) && picture !== failedPicture;

  if (showImage) {
    return (
      <img
        src={picture}
        className={`${sharedClassName} ${sizeData.className} ${className}`}
        alt="Rounded avatar"
        data-testid="avatar-image"
        onError={() => setFailedPicture(picture)}
      />
    );
  }

  return <AvatarPlaceholder size={size} className={className} iconClassName={iconClassName} iconName={iconName} />;
}

interface EditableAvatarProps {
  picture?: string;
  size?: keyof typeof sizes;
  onEditClick?: () => void;
  className?: string;
  iconName?: MaterialNames;
  editIconName?: MaterialNames;
}

export function EditableAvatar({
  picture,
  size = "medium",
  onEditClick,
  className,
  iconName = "person",
  editIconName = "photo_camera"
}: EditableAvatarProps) {
  const [failedPicture, setFailedPicture] = useState<string | undefined>();

  const sizeData = sizes[size];
  const showImage = Boolean(picture) && picture !== failedPicture;

  return (
    <div className={`relative group ${sizeData.className} ${className}`}>
      {showImage ? (
        <img
          src={picture}
          className={`${sharedClassName} ${sizeData.className}`}
          alt="Rounded avatar"
          data-testid="avatar-image"
          onError={() => setFailedPicture(picture)}
        />
      ) : (
        <AvatarPlaceholder size={size} iconName={iconName} iconClassName="text-sys-brand-on-surface-variant" />
      )}

      {onEditClick && (
        <div
          className="absolute inset-0 bg-black bg-opacity-50 rounded-full !self-auto flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
          onClick={onEditClick}
          data-testid="avatar-edit-overlay"
        >
          <Icon name={editIconName} size={sizeData.size * 0.6} className="text-white" />
        </div>
      )}
    </div>
  );
}
